  <template>
  <div class="knowledge-question-practice">
    <!-- 顶部导航栏 -->
    <div class="practice-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="practice-title">{{ currentKnowledge }} - {{ tt('practiceTitle') }}</h1>
      </div>
      <div class="header-right">
        <div v-if="practiceMode === 'step' && questionList.length > 0" class="timer-display" :style="{ marginRight: '15px', fontWeight: 'bold', color: timerColor }">
          <i class="el-icon-time"></i> {{ currentQuestionTime }}
        </div>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          @click="refreshQuestions"
          class="refresh-btn">
          {{ tt('refresh') }}
        </el-button>
        <el-radio-group v-model="langMode" size="small" style="margin-left: 10px;">
          <el-radio-button label="zh">中文</el-radio-button>
          <el-radio-button label="en">English</el-radio-button>
          <el-radio-button label="bi">双语</el-radio-button>
        </el-radio-group>
        <el-radio-group v-model="practiceMode" size="small" style="margin-left: 10px;">
          <el-radio-button label="step">逐题</el-radio-button>
          <el-radio-button label="list">列表</el-radio-button>
        </el-radio-group>

      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" :element-loading-text="tt('loading')" element-loading-background="rgba(0, 0, 0, 0.8)"></div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <i class="el-icon-warning error-icon"></i>
        <p class="error-message">{{ error }}</p>
        <el-button type="primary" @click="refreshQuestions">{{ tt('reload') }}</el-button>
      </div>
    </div>

    <!-- 题目练习区域 -->
    <div v-else-if="questionList.length > 0" class="question-practice-content">
      <!-- 统计信息 -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-number">{{ knowledgeProgress.total }}</div>
          <div class="stat-label">{{ tt('k_total') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ knowledgeProgress.completed }}</div>
          <div class="stat-label">{{ tt('k_completed') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ knowledgeProgress.correct }}</div>
          <div class="stat-label">{{ tt('k_correct') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ Math.round((knowledgeProgress.completionRate || 0) * 100) }}%</div>
          <div class="stat-label">{{ tt('k_completionRate') }}</div>
        </div>
      </div>

      <!-- 逐题模式 -->
      <div v-if="practiceMode === 'step' && currentQuestion" class="questions-container">
        <div class="question-item" :class="{ 'answered': userAnswers[currentIndex] || doneMap[currentQuestion.id] }">
          <div class="question-header">
            <span class="question-number">{{ formatQuestionNumber(currentIndex) }}</span>
            <div class="question-meta">
              <span class="question-knowledge">{{ getKnowledgeDisplay(currentQuestion.knowledge) }}</span>
              <span v-if="getQuestionSource(currentQuestion)" class="question-source">
                <i class="el-icon-document"></i> {{ getQuestionSource(currentQuestion) }}
              </span>
              <span class="question-difficulty" :class="getDifficultyClass(currentQuestion.difficulty)">
                {{ getDifficultyText(currentQuestion.difficulty) }}
              </span>
              <el-tag v-if="doneMap[currentQuestion.id]" size="mini" type="success">{{ tt('done') }}</el-tag>
            </div>
          </div>

          <div class="question-content" v-html="renderMathFormula(getQuestionContent(currentQuestion, langMode))"></div>

          <div v-if="getQuestionImages(currentQuestion).length > 0" class="question-image">
            <el-image
              v-for="(imgUrl, imgIdx) in getQuestionImages(currentQuestion)"
              :key="imgIdx"
              :src="resolveImageUrl(imgUrl)"
              :preview-src-list="getQuestionImages(currentQuestion).map(u => resolveImageUrl(u))"
              fit="contain"
              style="max-width: 360px; max-height: 260px; margin: 8px 0; border: 1px solid #f0f0f0;"
            />
          </div>

          <!-- 选项显示（逐题） -->
          <div class="question-options" v-if="getOptions(currentQuestion).length > 0">
            <el-radio-group v-model="userAnswers[currentIndex]" @change="onAnswerChange(currentIndex)">
              <el-radio
                v-for="(option, optionIndex) in getOptions(currentQuestion)"
                :key="option.key"
                :label="option.key"
                class="option-item"
              >
                <template v-if="getOptionImage(currentQuestion, optionIndex)">
                  <div class="option-image-wrap">
                    <span class="option-key-label">{{ option.key }}.</span>
                    <el-image
                      :src="resolveImageUrl(getOptionImage(currentQuestion, optionIndex))"
                      :preview-src-list="[resolveImageUrl(getOptionImage(currentQuestion, optionIndex))]"
                      fit="contain"
                      class="option-image-preview"
                    />
                  </div>
                </template>
                <template v-else>
                  <span v-html="renderMathFormula(option.key + '. ' + option.value)"></span>
                </template>
              </el-radio>
            </el-radio-group>
          </div>

          <!-- 如果没有选项，显示答案输入框（逐题） -->
          <div class="question-answer-input" v-else>
            <el-input
              v-model="userAnswers[currentIndex]"
              @input="onAnswerChange(currentIndex)"
              :placeholder="tt('inputAnswer')"
              maxlength="10"
              show-word-limit
              style="width: 200px"
            ></el-input>
            <span class="answer-tip">{{ tt('answerTip') }}</span>
          </div>

          <div v-if="userAnswers[currentIndex] && (showAnswers || revealedQuestions[currentIndex])" class="correct-answer">
            <div class="answer-label">{{ tt('correctAnswer') }}</div>
            <div class="answer-value">{{ currentQuestion.answer || '暂无答案' }}</div>
          </div>

          <div v-if="userAnswers[currentIndex] && (showAnswers || revealedQuestions[currentIndex]) && getQuestionAnalysis(currentQuestion)" class="question-analysis">
            <div class="analysis-title">{{ tt('analysis') }}</div>
            <div class="analysis-content" v-html="renderMathFormula(getQuestionAnalysis(currentQuestion))"></div>
          </div>
        </div>
      </div>

      <!-- 题目区域：根据模式切换 逐题 / 列表 -->
      <!-- 列表模式 -->
      <div v-else class="questions-container">
        <div
          v-for="(question, index) in questionList"
          :key="question.id"
          class="question-item"
          :class="{ 'answered': userAnswers[index] || doneMap[question.id] }"
        >
          <div class="question-header">
            <span class="question-number">{{ formatQuestionNumber(index) }}</span>
            <el-tag v-if="doneMap[question.id]" size="mini" type="success" style="margin-left:8px;">{{ tt('done') }}</el-tag>
            <div class="question-meta">
              <span class="question-knowledge">{{ getKnowledgeDisplay(question.knowledge) }}</span>
              <span v-if="getQuestionSource(question)" class="question-source">
                <i class="el-icon-document"></i> {{ getQuestionSource(question) }}
              </span>
              <span class="question-difficulty" :class="getDifficultyClass(question.difficulty)">
                {{ getDifficultyText(question.difficulty) }}
              </span>
              <el-tag v-if="doneMap[question.id]" size="mini" type="success">{{ tt('done') }}</el-tag>
              <span v-if="userAnswers[index] && (showAnswers || revealedQuestions[index])" class="answer-status" :class="getAnswerStatusClass(index)">
                {{ getAnswerStatusText(index) }}
              </span>
            </div>
          </div>

          <div class="question-content" v-html="renderMathFormula(getQuestionContent(question, langMode))"></div>

          <!-- 题目图片展示（若有），放在中英文题干下面 -->
          <div v-if="getQuestionImages(question).length > 0" class="question-image">
            <el-image
              v-for="(imgUrl, imgIdx) in getQuestionImages(question)"
              :key="imgIdx"
              :src="resolveImageUrl(imgUrl)"
              :preview-src-list="getQuestionImages(question).map(u => resolveImageUrl(u))"
              fit="contain"
              style="max-width: 360px; max-height: 260px; margin: 8px 0; border: 1px solid #f0f0f0;"
            />
          </div>

          <!-- 图片上传区域 -->
          <div class="image-upload-section">
            <div class="upload-button-container">
              <el-upload
                class="image-uploader"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :show-file-list="false"
                :on-success="(response, file) => handleImageUploadSuccess(response, file, index)"
                :on-error="handleImageUploadError"
                :before-upload="beforeImageUpload"
                accept="image/*">
                <el-button type="primary" icon="el-icon-upload" size="small">{{ tt('uploadImage') }}</el-button>
              </el-upload>
              <span class="upload-tip">{{ tt('uploadStepsTip') }}</span>
            </div>

            <!-- 已上传的图片预览 -->
            <div v-if="questionImages[index] && questionImages[index].length > 0" class="uploaded-images">
              <div class="image-preview-title">{{ tt('uploadedImages') }}</div>
              <div class="image-list">
                <div
                  v-for="(image, imgIndex) in questionImages[index]"
                  :key="imgIndex"
                  class="image-item">
                  <img :src="image.url" :alt="tt('previewImageAlt') + (imgIndex + 1)" />
                  <div class="image-actions">
                    <el-button
                      type="text"
                      icon="el-icon-view"
                      size="mini"
                      @click="previewImage(image.url)">
                      {{ tt('preview') }}
                    </el-button>
                    <el-button
                      type="text"
                      icon="el-icon-delete"
                      size="mini"
                      @click="removeImage(index, imgIndex)">
                      {{ tt('delete') }}
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 选项显示 -->
          <div class="question-options" v-if="getOptions(question).length > 0">
            <el-radio-group v-model="userAnswers[index]" @change="onAnswerChange(index)">
              <el-radio
                v-for="(option, optionIndex) in getOptions(question)"
                :key="option.key"
                :label="option.key"
                class="option-item"
              >
                <template v-if="getOptionImage(question, optionIndex)">
                  <div class="option-image-wrap">
                    <span class="option-key-label">{{ option.key }}.</span>
                    <el-image
                      :src="resolveImageUrl(getOptionImage(question, optionIndex))"
                      :preview-src-list="[resolveImageUrl(getOptionImage(question, optionIndex))]"
                      fit="contain"
                      class="option-image-preview"
                    />
                  </div>
                </template>
                <template v-else>
                  <span v-html="renderMathFormula(option.key + '. ' + option.value)"></span>
                </template>
              </el-radio>
            </el-radio-group>
          </div>

          <!-- 如果没有选项，显示答案输入框 -->
          <div class="question-answer-input" v-else>
            <el-input
              v-model="userAnswers[index]"
              @input="onAnswerChange(index)"
              :placeholder="tt('inputAnswer')"
              maxlength="10"
              show-word-limit
              style="width: 200px"
            ></el-input>
            <span class="answer-tip">{{ tt('answerTip') }}</span>
          </div>

          <div v-if="userAnswers[currentIndex] && (showAnswers || revealedQuestions[currentIndex])" class="correct-answer">
            <div class="answer-label">{{ tt('correctAnswer') }}</div>
            <div class="answer-value">{{ currentQuestion.answer || '暂无答案' }}</div>
          </div>

          <div v-if="userAnswers[currentIndex] && (showAnswers || revealedQuestions[currentIndex]) && getQuestionAnalysis(currentQuestion)" class="question-analysis">
            <div class="analysis-title">{{ tt('analysis') }}</div>
            <div class="analysis-content" v-html="renderMathFormula(getQuestionAnalysis(currentQuestion))"></div>
          </div>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div class="action-section">
        <div v-if="practiceMode === 'step'" class="step-controls">
          <span class="step-progress">{{ currentIndex + 1 }}/{{ questionList.length }}</span>
          <el-button @click="prevQuestion" :disabled="currentIndex === 0">{{ tt('prev') }}</el-button>
          <el-button @click="nextQuestion" :disabled="currentIndex >= questionList.length - 1">{{ tt('next') }}</el-button>
          <el-button type="primary" @click="submitCurrentAnswer" :disabled="!userAnswers[currentIndex] || String(userAnswers[currentIndex]).trim() === ''">{{ tt('submitCurrent') }}</el-button>
        </div>
        <el-button
          type="success"
          size="large"
          @click="submitAnswers"
          :disabled="answeredCount === 0"
          :loading="submitting">
          {{ tt('submitAll') }}
        </el-button>
        <el-button
          type="warning"
          size="large"
          @click="resetAnswers">
          {{ tt('reset') }}
        </el-button>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <div class="empty-content">
        <i class="el-icon-document empty-icon"></i>
        <p class="empty-message">{{ tt('emptyKnowledge') }}</p>
        <el-button type="primary" @click="goBack">{{ tt('backKnowledge') }}</el-button>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      :title="tt('imagePreview')"
      :visible.sync="showImagePreview"
      width="auto"
      :close-on-click-modal="true">
      <div class="image-preview-dialog">
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </el-dialog>

    <!-- 答题结果弹窗 -->
    <el-dialog
      :title="tt('resultTitle')"
      :visible.sync="showResultDialog"
      width="70%"
      :before-close="handleCloseResultDialog"
      class="result-dialog"
    >
      <div class="result-dialog-content">
        <div class="result-summary">
          <div class="score-display">
            <div class="score-number">{{ finalScore }}</div>
            <div class="score-label">{{ scoreUnit }}</div>
          </div>
          <div class="score-details">
            <div class="detail-item">
              <span class="detail-label">{{ tt('totalLabel') }}</span>
              <span class="detail-value">{{ questionResults.length }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ tt('correctLabel') }}</span>
              <span class="detail-value correct">{{ correctCount }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ tt('wrongLabel') }}</span>
              <span class="detail-value incorrect">{{ wrongCount }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ tt('accuracyLabel') }}</span>
              <span class="detail-value">{{ accuracy }}%</span>
            </div>
          </div>
        </div>

        <!-- 错题列表 -->
        <div v-if="wrongQuestions.length > 0" class="wrong-questions-section">
          <h4>{{ tt('wrongAnalysis') }}</h4>
          <div
            v-for="result in wrongQuestions"
            :key="result.questionId"
            class="wrong-question-item"
          >
            <div class="question-header">
              <span class="question-number">{{ formatQuestionNumber(getOriginalQuestionIndex(result.questionId)) }}</span>
              <span class="question-knowledge">{{ getKnowledgeDisplay(result.knowledge) }}</span>
            </div>

            <div class="question-content" v-html="result.questionContent"></div>

            <div class="answer-details">
              <div class="answer-row">
                <span class="answer-label">你的答案：</span>
                <span class="answer-value incorrect">{{ result.userAnswer }}</span>
              </div>
              <div class="answer-row">
                <span class="answer-label">正确答案：</span>
                <span class="answer-value correct">{{ result.correctAnswer }}</span>
              </div>
            </div>

            <div class="analysis-section" v-if="result.analysis">
              <div class="analysis-title">{{ tt('analysis') }}</div>
              <div class="analysis-content" v-html="renderMathFormula(result.analysis)"></div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showResultDialog = false">{{ tt('close') }}</el-button>
        <el-button type="primary" @click="retryPractice">{{ tt('retry') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getAllByRepoId as getCourseQuestionsByRepoId } from '@/api/courseQuestion'
import { getKnowledgeProgressByRepoId } from '@/api/knowledge'
import { addRecord as addUserQuestionRecord, getRecordsByUserId as getUserQuestionRecordsByUser } from '@/api/userQuestionRecord'
import { getProgressByDate, upsertTodayPlan, updateProgressFinished, getProgressPaging, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds as updateSchemeFinished } from '@/api/studyPlan'
import { userStatusAdd } from '@/api/userStatus'
import { getTokenInfo } from '@/utils/jwtUtils'
import { getMinioUrl } from '@/utils/minio'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'KnowledgeQuestionPractice',
  data() {
    return {
      // 加载状态
      loading: false,
      // 错误信息
      error: null,
      // 当前知识点
      currentKnowledge: '',
      // 题目列表
      questionList: [],
      // 用户答案
      userAnswers: [],
      // 提交状态
      submitting: false,
      // 是否显示答案
      showAnswers: false,
      // 结果弹窗
      showResultDialog: false,
      // 答题结果
      questionResults: [],
      // 错题列表
      wrongQuestions: [],

      // 图片上传相关
      questionImages: [], // 每道题的图片数组
      showImagePreview: false,
      previewImageUrl: '',
      uploadUrl: '/api/upload/image', // 根据实际后端接口调整
      // 题目显示语言模式：zh(中文)/en(英文)/bi(双语)
      langMode: 'bi',
      // 知识点名称缓存：id -> name（通用题目可能直接返回文本知识点，这里仅做兼容）
      _knowledgeIdNameMap: {},
      // 子级id -> 父级名称 映射
      _knowledgeParentNameMap: {},
      // id -> parentId 映射（可选）
      _knowledgeIdToParentId: {},
      // 名称 -> id 映射（用于当传入的是名称时反查ID）
      _knowledgeNameToIdMap: {},
      // 用户/题库上下文（用于写入记录）
      currentUserId: null,
      repoId: null,
      source: null,
      // 知识点进度
      knowledgeProgress: {
        total: 0,
        completed: 0,
        correct: 0,
        completionRate: 0,
        accuracyRate: 0
      },
      statCorrect: 0,
      statWrong: 0,
      statProgressId: null,
      statFinishedIds: [],

    // 从服务端读取历史做题记录并合并到 doneMap
    async fetchDoneFromServer() {
      try {
        const userId = this.currentUserId
        if (!userId) return
        const repoId = this.repoId || (this.$route.query && this.$route.query.repoId)
        const resp = await getUserQuestionRecordsByUser(userId, repoId)
        const raw = (resp && resp.data && resp.data.data) || resp?.data || resp || []
        const list = Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : [])
        if (!Array.isArray(list) || list.length === 0) return
        const questionIdSet = new Set(this.questionList.map(q => Number(q.id)))
        list.forEach(r => {
          const qid = Number(r.quesId || r.questionId || r.ques_id || r.question_id)
          if (!isNaN(qid) && questionIdSet.has(qid)) {
            this.$set(this.doneMap, qid, true)
          }
        })
        this.saveProgress()
      } catch (_) { /* ignore */ }
    },
      // 已完成题目标记（题ID -> true）
      doneMap: {},
      // 展示答案开关（逐题揭示用）
      revealedQuestions: [],
      // 练习模式 list/step 与当前题索引
      practiceMode: 'step',
      currentIndex: 0,
      // 计时器
      currentStartTime: null, // 当前题目开始时间
      questionDurations: [], // 每题用时（秒）
      timerInterval: null, // 计时器间隔
      currentQuestionTime: '00:00', // 当前题目用时显示
      timerColor: '#409EFF' // 计时器颜色
    }
  },
  computed: {
    // 动态获取上传头部信息
    uploadHeaders() {
      return {
        'Authorization': `Bearer ${this.$store.getters.token}`
      }
    },

    // 已答题数
    answeredCount() {
      return this.userAnswers.filter(answer => answer && answer.trim() !== '').length
    },
    // 答对题数
    correctCount() {
      return this.questionResults.filter(result => result.isCorrect).length
    },
    // 答错题数
    wrongCount() {
      return this.questionResults.filter(result => !result.isCorrect).length
    },
    // 正确率
    accuracy() {
      if (this.answeredCount === 0) return 0
      return Math.round((this.correctCount / this.answeredCount) * 100)
    },
    // 最终得分（以已提交题数为分母）
    finalScore() {
      const total = this.questionResults.length
      if (total === 0) return 0
      return Math.round((this.correctCount / total) * 100)
    },
    // 当前题目
    currentQuestion() {
      if (!Array.isArray(this.questionList) || this.questionList.length === 0) return null
      const i = Math.min(Math.max(this.currentIndex, 0), this.questionList.length - 1)
      return this.questionList[i]
    },
    // 分数单位
    scoreUnit() {
      return this.langMode === 'en' ? '' : '分'
    }
  },
  async created() {
    this.hideSidebarAndNavbar()
    this.source = 'general'
    // 初始化用户与题库上下文
    try {
      const info = getTokenInfo()
      this.currentUserId = info && info.id ? info.id : null
    } catch (_) { this.currentUserId = null }
    this.repoId = this.$route.query && this.$route.query.repoId ? this.$route.query.repoId : null
    // 并行初始化：题目与进度
    this.loadQuestions()
    this.loadKnowledgeProgress()
  },
  watch: {
    // 监听当前题目变化，设置题目上下文供AI助手使用
    currentQuestion: {
      handler(question) {
        this.setQuestionContext(question)
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    // 清除题目上下文
    window.__currentQuestionContext = null
    try { localStorage.removeItem('currentQuestionContext') } catch (e) {}
    // this.showSidebarAndNavbar()
  },
  methods: {
    // 设置当前题目上下文供AI助手使用
    setQuestionContext(question) {
      if (question) {
        const ctx = {
          type: 'question',
          id: question.id,
          content: this.getQuestionContent(question, 'zh') || this.getQuestionContent(question, 'en'),
          options: this.getOptions(question),
          knowledge: question.knowledge || this.currentKnowledge,
          difficulty: question.difficulty,
          answer: question.answer,
          analysis: this.getQuestionAnalysis(question)
        }
        window.__currentQuestionContext = ctx
        try {
          localStorage.setItem('currentQuestionContext', JSON.stringify(ctx))
        } catch (e) {}
      }
    },
    // 加载当前知识点的进度数据
    async loadKnowledgeProgress() {
      try {
        const repoId = this.repoId || (this.$route.query && this.$route.query.repoId)
        const knowledgeId = this.$route.query && this.$route.query.knowledgeId
        if (!repoId || !knowledgeId) return
        const resp = await getKnowledgeProgressByRepoId(repoId)
        const payload = (resp && resp.data && resp.data.data) || resp?.data || resp || {}
        const list = Array.isArray(payload?.knowledgeList) ? payload.knowledgeList : []
        const kid = Number(knowledgeId)
        const item = list.find(x => Number(x.id) === kid)
        if (item) {
          // 不使用后端返回的total（可能是缓存的旧数据），保持使用questionList.length
          const currentTotal = this.knowledgeProgress.total || 0
          const completed = Number(item.completed || 0)
          const correct = Number(item.correct || 0)
          const completionRate = currentTotal > 0 ? (completed / currentTotal) : 0
          const accuracyRate = completed > 0 ? (correct / completed) : 0
          this.knowledgeProgress = { total: currentTotal, completed, correct, completionRate, accuracyRate }
        } else {
          this.knowledgeProgress = { total: this.knowledgeProgress.total || 0, completed: 0, correct: 0, completionRate: 0, accuracyRate: 0 }
        }
      } catch (e) {
        // 静默失败，不影响做题
        this.knowledgeProgress = { total: this.knowledgeProgress.total || 0, completed: 0, correct: 0, completionRate: 0, accuracyRate: 0 }
      }
    },
    // 获取题目的所有图片路径
    getQuestionImages(question) {
      if (!question) return []
      const images = []

      // 1. 检查 imagePaths 字段（JSON 字符串格式）
      const imagePaths = question.imagePaths || question.image_paths
      if (imagePaths) {
        try {
          const paths = typeof imagePaths === 'string' ? JSON.parse(imagePaths) : imagePaths
          if (Array.isArray(paths)) {
            images.push(...paths.filter(p => p))
          }
        } catch (e) {
          // 如果解析失败，尝试作为单个路径
          if (typeof imagePaths === 'string' && imagePaths.trim()) {
            images.push(...imagePaths.split(/[;,]/).map(p => p.trim()).filter(Boolean))
          } else if (Array.isArray(imagePaths)) {
            images.push(...imagePaths.filter(p => p))
          }
        }
      }

      // 2. 检查 image 字段（向后兼容）
      if (question.image && !images.includes(question.image)) {
        images.push(question.image)
      }

      // 3. 检查 url 字段（向后兼容）
      if (question.url && !images.includes(question.url)) {
        images.push(question.url)
      }

      return images.filter(img => img && img.trim())
    },
    getOptionImage(question, optionIndex) {
      if (!question) return ''
      const raw = question.optionImages || question.option_images
      if (!raw) return ''
      let images = []
      if (Array.isArray(raw)) {
        images = raw.map(item => String(item || '').trim()).filter(Boolean)
      } else if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return ''
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            images = parsed.map(item => String(item || '').trim()).filter(Boolean)
          }
        } catch (e) {
          images = trimmed.split(/[;,]/).map(item => item.trim()).filter(Boolean)
        }
      }
      return images[optionIndex] || ''
    },
    // 相对路径图片补全前缀
    resolveImageUrl(path) {
      if (!path) return ''
      const s = String(path)
      const isAbs = /^(https?:)?\/\//i.test(s)
      if (isAbs) {
        try { console.debug('[KQP][img] abs src =', s) } catch (_) {}
        return s
      }
      // 已是后端代理
      if (s.startsWith('/api/file/')) return s
      // 若是 /{bucket}/{key} 形式，转 MinIO 直链或代理
      if (/^\/[A-Za-z0-9_-]+\//.test(s)) {
        try {
          return getMinioUrl(s)
        } catch (_) {}
      }
      const base = process.env.VUE_APP_BASE_API || ''
      try {
        // 将相对 base 转为绝对，以便 URL() 正确解析
        const absBase = /^(https?:)?\/\//i.test(base)
          ? base
          : (window.location.origin + (base.startsWith('/') ? base : ('/' + base)))
        const url = new URL(s, absBase)
        const out = url.toString()
        console.debug('[KQP][img] rel src =', s, ' base =', absBase, ' -> ', out)
        return out
      } catch (_) {
        // 兜底：简单拼接并去重多余斜杠与重复的 /api
        try {
          const out = (base ? (base.replace(/\/$/, '') + '/') : '') + s.replace(/^\//, '')
          return out
        } catch (e) {
          return s
        }
      }
    },

    // 隐藏侧边栏和导航栏
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = 'none'
      }
      if (navbar) {
        navbar.style.display = 'none'
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },

    // 恢复侧边栏和导航栏
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = ''
      }
      if (navbar) {
        navbar.style.display = ''
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },

    // 加载题目数据
    async loadQuestions() {
      this.loading = true
      this.error = null

      try {
        // 从路由参数获取知识点与题库ID（知识点可为空）
        const { knowledge, knowledgeId, repoId, otherParentId } = this.$route.query
        console.log('[KQP] loadQuestions 被调用，参数:', { knowledge, knowledgeId, repoId, otherParentId })
        this.source = 'general'

        if (!knowledge && !knowledgeId && !otherParentId) {
          throw new Error('缺少必要参数，请从知识点列表进入')
        }

        this.currentKnowledge = knowledge

        // “其他”模式：取题库全部题目后在前端筛选（仅保留最深知识点刚好等于 otherParentId 的题目）
        if (otherParentId != null && String(otherParentId).trim() !== '') {
          const pid = Number(otherParentId)
          if (isNaN(pid)) throw new Error('其他知识点参数错误')
          console.log('[KQP] 其他模式，准备调用API获取全量题目，repoId:', repoId)
          const repoResp = await getCourseQuestionsByRepoId(repoId, null)
          const payload = (repoResp && repoResp.data && repoResp.data.data) || repoResp?.data || repoResp || []
          const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.list) ? payload.list : [])
          const onlyParent = list.filter(q => {
            const raw = q.knowledge != null ? q.knowledge : (q.knowledgeId != null ? q.knowledgeId : (q.knowledge_id != null ? q.knowledge_id : null))
            if (raw == null || raw === '') return false
            const ids = String(raw)
              .split(/[,，\s]+/)
              .map(x => String(x).trim())
              .filter(x => x && /^\d+$/.test(x))
              .map(x => Number(x))
            if (!ids.length) return false
            return ids[ids.length - 1] === pid
          })
          console.log('[KQP] 其他模式筛选后题目数量:', onlyParent.length)
          this.applyQuestions(onlyParent)
          return
        }

        // 正常模式：直接传递 knowledgeId 给后端筛选
        const kid = knowledgeId != null && String(knowledgeId).trim() !== '' ? Number(knowledgeId) : null
        console.log('[KQP] 准备调用API，repoId:', repoId, 'knowledgeId:', kid)
        const repoResp = await getCourseQuestionsByRepoId(repoId, kid)
        const payload = (repoResp && repoResp.data && repoResp.data.data) || repoResp?.data || repoResp || []
        const filtered = Array.isArray(payload) ? payload : (Array.isArray(payload?.list) ? payload.list : [])
        console.log('[KQP] API返回题目数量:', filtered.length)

        this.applyQuestions(filtered)
        return
      } catch (error) {
        console.error('加载题目失败:', error)
        this.error = error.message || this.tt('loadFailed')
        this.questionList = []
      } finally {
        this.loading = false
      }
    },

    applyQuestions(rawList) {
      // 处理题目数据，统一字段，解析选项并初始化相关状态
      const list = (rawList || []).map(q => {
        const content = q.content || q.contentCn || q.contentEn || ''
        const analysis = q.analysis || q.analysisCn || q.analysisEn || ''
        const optionsSource = q.optionsCn || q.options_cn || q.optionsEn || q.options_en || q.optionsStr || q.options || ''
        if (optionsSource) {
          const options = this.parseOptions(optionsSource)
          return { ...q, content, analysis, options }
        }
        return { ...q, content, analysis }
      })
      this.questionList = list

      // 更新知识点进度的总题数为实际筛选后的题目数量
      this.knowledgeProgress.total = this.questionList.length

      // 先根据题目数量初始化用户答案数组
      this.userAnswers = new Array(this.questionList.length).fill('')
      this.questionImages = new Array(this.questionList.length).fill(null).map(() => [])
      this.questionResults = []
      this.revealedQuestions = new Array(this.questionList.length).fill(false)
      this.currentIndex = 0
      // 初始化计时器
      this.questionDurations = new Array(this.questionList.length).fill(0)
      this.startQuestionTimer()
      console.log(`[KQP] 成功获取 ${this.questionList.length} 道题目`)
      // 若预取未命中，兜底按需拉取缺失名称
      this.refreshKnowledgeNamesFromQuestions()
      // 先恢复本地进度（主要来源）
      this.restoreProgress()
      // 再把后端返回的 hasDone / userAnswer 合并进来：
      // - 若后端认为已做，则补充/覆盖本地 doneMap；
      // - 若后端明确未做(hasDone=0)，且无 userAnswer，则以后端为准，清除本地记录，避免误判“已做”。
      this.questionList.forEach((q, idx) => {
        // 1) 已选答案回显
        const backendUserAnswer = q.userAnswer != null ? q.userAnswer : q.user_answer
        const backendAnswerStr = backendUserAnswer != null ? String(backendUserAnswer).trim() : ''

        // 2) 已做题标记（以 hasDone 为主）
        const rawHasDone = q.hasDone != null ? q.hasDone : q.has_done
        const hasDoneFlag = rawHasDone === 1 || rawHasDone === '1' || rawHasDone === true
        const notDoneFlag = rawHasDone === 0 || rawHasDone === '0' || rawHasDone === false

        if (hasDoneFlag) {
          // 后端认为已做：确保 doneMap 为 true
          if (!this.doneMap[q.id]) {
            this.$set(this.doneMap, q.id, true)
          }
          // 若本地没有答案而后端有答案，则回显后端答案
          if (!this.userAnswers[idx] && backendAnswerStr) {
            this.$set(this.userAnswers, idx, backendAnswerStr)
          }
        } else if (notDoneFlag && !backendAnswerStr) {
          // 后端明确认为未做，且没有 userAnswer：以后端为准，清除本地记录
          if (this.doneMap[q.id]) this.$delete(this.doneMap, q.id)
          if (this.userAnswers[idx]) this.$set(this.userAnswers, idx, '')
        } else if (backendAnswerStr && !this.userAnswers[idx]) {
          // hasDone 未给出或为 null，但后端有答案，则仅回显答案，不动 doneMap
          this.$set(this.userAnswers, idx, backendAnswerStr)
        }
      })
      // 再从服务端合并历史记录并更新定位
      this.fetchDoneFromServer().then(() => {
        const idx = this.questionList.findIndex(q => !this.doneMap[q.id])
        if (idx >= 0 && idx !== this.currentIndex) {
          this.currentIndex = idx
          this.startQuestionTimer()
        }
      }).catch(() => {})
    },

    // 将 knowledge 字段（可能是数字或逗号分隔）映射为名称
    getKnowledgeDisplay(field) {
      if (!field) return '—'
      const map = this._knowledgeIdNameMap || {}
      if (typeof field === 'number') {
        const name = map[field]
        return name || `知识点${field}`
      }
      const ids = String(field)
        .split(/[,，]/)
        .map(s => Number(String(s).trim()))
        .filter(n => !isNaN(n) && n > 0)
      if (ids.length === 0) return field
      const names = ids.map(id => {
        const name = map[id]
        return name || `知识点${id}`
      })
      return names.join(', ')
    },

    // 获取题目来源信息（年份 + 题号）
    getQuestionSource(question) {
      if (!question) return ''
      const parts = []

      // 年份信息
      if (question.year) {
        parts.push(`${question.year}年`)
      }

      // 题号信息（从 number 或 source 字段提取）
      if (question.number) {
        const num = String(question.number).trim()
        // 如果 number 包含题号信息（如 "AMC8-2023-1" 或 "第1题"）
        const match = num.match(/第?(\d+)题?|-(\d+)$/)
        if (match) {
          const qNum = match[1] || match[2]
          parts.push(`第${qNum}题`)
        }
      } else if (question.source) {
        // 从 source 字段提取题号
        const sourceMatch = String(question.source).match(/第?(\d+)题/)
        if (sourceMatch) {
          parts.push(`第${sourceMatch[1]}题`)
        }
      }

      return parts.length > 0 ? parts.join(' ') : ''
    },

    // 将 knowledge 映射为 “父级-子级” 文本，若无父级则仅子级名
    getKnowledgeDisplayWithParent(field) {
      if (!field) return '—'
      const nameMap = this._knowledgeIdNameMap || {}
      const parentNameMap = this._knowledgeParentNameMap || {}
      const idToParent = this._knowledgeIdToParentId || {}
      const nameToId = this._knowledgeNameToIdMap || {}
      const toIds = (val) => {
        if (typeof val === 'number') return [val]
        return String(val)
          .split(/[,，]/)
          .map(s => Number(String(s).trim()))
          .filter(n => !isNaN(n) && n > 0)
      }
      let ids = toIds(field)
      // 若不是ID集合（e.g. 已是中文名称列表），尝试名称映射到ID
      if (ids.length === 0) {
        const names = String(field)
          .split(/[,，]/)
          .map(s => String(s).trim())
          .filter(s => !!s)
        ids = names.map(n => nameToId[n]).filter(v => v != null)
        if (ids.length === 0) return String(field)
      }
      // 将ID拆分为：有父级的子ID集合 和 纯父级ID集合
      const childIds = []
      const parentOnlyIds = new Set()
      ids.forEach(id => {
        const pid = idToParent[id]
        if (pid && nameMap[pid]) {
          childIds.push({ id, pid })
        } else {
          parentOnlyIds.add(id)
        }
      })
      // 如果某个父级在本次集合中已经有子级出现，则不再单独展示父级名称
      childIds.forEach(({ pid }) => { if (parentOnlyIds.has(pid)) parentOnlyIds.delete(pid) })
      // 组装显示：先输出“父-子”对，再输出确实没有任何子级的父级名称
      const parts = []
      childIds.forEach(({ id, pid }) => {
        const parent = nameMap[pid] || parentNameMap[id] || pid
        const child = nameMap[id] || id
        parts.push(`${parent}-${child}`)
      })
      parentOnlyIds.forEach(pid => {
        const parent = nameMap[pid] || pid
        parts.push(`${parent}`)
      })
      // 去重并稳定顺序
      const seen = new Set()
      const uniq = []
      parts.forEach(p => { if (!seen.has(p)) { seen.add(p); uniq.push(p) } })
      return uniq.join(', ')
    },

    // 为当前题目集中的知识点ID按需加载名称
    async refreshKnowledgeNamesFromQuestions() {
      const repoId = this.$route?.query?.repoId
      if (!repoId) return

      try {
        // 调用知识点树API获取完整的知识点结构
        const { listGeneralKnowledgeTree } = await import('@/api/knowledge')
        const resp = await listGeneralKnowledgeTree(repoId)
        const tree = resp?.data?.data || resp?.data || []

        if (!Array.isArray(tree) || tree.length === 0) {
          console.warn('[KQP] 未获取到知识点树数据')
          return
        }

        // 递归遍历树，建立 ID -> 名称 的映射
        const idNameMap = {}
        const idParentIdMap = {}
        const parentNameMap = {}
        const nameToIdMap = {}

        const traverse = (nodes, parentId = null, parentName = '') => {
          if (!Array.isArray(nodes)) return
          nodes.forEach(node => {
            if (node.id && node.content) {
              idNameMap[node.id] = node.content
              nameToIdMap[node.content] = node.id

              if (parentId) {
                idParentIdMap[node.id] = parentId
                if (parentName) {
                  parentNameMap[node.id] = parentName
                }
              }

              // 递归处理子节点
              if (node.children && node.children.length > 0) {
                traverse(node.children, node.id, node.content)
              }
            }
          })
        }

        traverse(tree)

        // 更新组件的映射数据
        this._knowledgeIdNameMap = idNameMap
        this._knowledgeIdToParentId = idParentIdMap
        this._knowledgeParentNameMap = parentNameMap
        this._knowledgeNameToIdMap = nameToIdMap

        console.log('[KQP] 知识点映射已建立，共', Object.keys(idNameMap).length, '个知识点')
      } catch (error) {
        console.error('[KQP] 获取知识点树失败:', error)
      }
    },

    // 解析选项：兼容数组/对象/字符串多种格式
    parseOptions(raw) {
      if (!raw) return []
      // 数组
      if (Array.isArray(raw)) {
        return raw.map(item => {
          if (typeof item === 'string') {
            const m = item.match(/^\s*\(?([A-Ea-e])\)?[\.|\)]?\s*(.*)$/)
            return m ? { key: m[1].toUpperCase(), value: (m[2] || '').trim() } : { key: '', value: item }
          }
          if (item && typeof item === 'object') {
            const k = (item.key || item.label || '').toString().toUpperCase()
            const v = item.value != null ? String(item.value) : (item.text || '')
            return { key: k, value: (v || '').trim() }
          }
          return { key: '', value: String(item) }
        }).filter(o => o.key)
      }
      // 对象 {A:'...',B:'...'}
      if (typeof raw === 'object') {
        return Object.keys(raw).map(k => ({ key: k.toUpperCase(), value: String(raw[k] || '').trim() }))
      }
      // 字符串
      const s = String(raw)
      const list = []
      // (A) xxx; (B) yyy
      const reParen = /\(([A-Ea-e])\)\s*([^;\n\r]*)/g
      let m
      while ((m = reParen.exec(s)) !== null) {
        list.push({ key: m[1].toUpperCase(), value: (m[2] || '').trim() })
      }
      if (list.length) return list
      // A. xxx 或 A) xxx，按分号/换行/BR 分割
      const parts = s.split(/\n|;|<br\s*\/>|<br>/i).map(x => x.trim()).filter(Boolean)
      for (const p of parts) {
        const m2 = p.match(/^\s*([A-Ea-e])\s*[\.|\)]\s*(.*)$/)
        if (m2) list.push({ key: m2[1].toUpperCase(), value: (m2[2] || '').trim() })
      }
      return list
    },
    // 统一获取当前语言下的选项
    getOptions(question) {
      if (!question) return []
      let source = null
      if (this.langMode === 'en') {
        source = question.optionsEn || question.options_en || null
      } else if (this.langMode === 'zh') {
        source = question.optionsCn || question.options_cn || null
      } else { // bi
        source = question.optionsCn || question.optionsEn || null
      }
      if (!source) source = question.options || question.optionsStr || question.option || ''
      return this.parseOptions(source)
    },

    // 按语言模式获取题干
    getQuestionContent(question, mode) {
      const m = mode || this.langMode
      const cn = question.contentCn || ''
      const en = question.contentEn || ''
      // 兼容已归一字段
      const uni = question.content || ''
      if (m === 'zh') return cn || uni
      if (m === 'en') return en || uni
      // 双语
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en || uni
    },

    // 按语言模式获取解析
    getQuestionAnalysis(question, mode) {
      const m = mode || this.langMode
      const cn = question.analysisCn || ''
      const en = question.analysisEn || ''
      // 兼容已归一字段
      const uni = question.analysis || ''
      if (m === 'zh') return cn || uni
      if (m === 'en') return en || uni
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en || uni
    },

    // 简易多语言
    tt(key) {
      const zh = {
        practiceTitle: '题目练习',
        refresh: '刷新题目',
        loading: '正在加载题目...',
        reload: '重新加载',
        k_total: '本知识点题目数',
        k_completed: '本知识点已完成',
        k_correct: '本知识点答对',
        k_completionRate: '本知识点完成率',
        uploadImage: '上传图片',
        uploadStepsTip: '上传解题步骤（可选）',
        uploadedImages: '已上传的图片：',
        previewImageAlt: '上传图片',
        preview: '预览',
        delete: '删除',
        inputAnswer: '请输入答案',
        answerTip: '（请输入答案，如：A、B、C、D、E 或具体数值）',
        correctAnswer: '正确答案：',
        analysis: '解析：',
        prev: '上一题',
        next: '下一题',
        submitCurrent: '提交本题',
        submitAll: '提交已作答',
        reset: '重置答案',
        emptyKnowledge: '该知识点暂无题目',
        backKnowledge: '返回知识点列表',
        imagePreview: '图片预览',
        resultTitle: '答题结果',
        close: '关闭',
        retry: '重新练习',
        totalLabel: '总题数：',
        correctLabel: '答对：',
        wrongLabel: '答错：',
        accuracyLabel: '正确率：',
        done: '已做',
        correctShort: '正确',
        wrongShort: '错误'
      }
      const en = {
        practiceTitle: 'Practice',
        refresh: 'Refresh',
        loading: 'Loading questions...',
        reload: 'Reload',
        k_total: 'Total (this topic)',
        k_completed: 'Completed',
        k_correct: 'Correct',
        k_completionRate: 'Completion rate',
        uploadImage: 'Upload Image',
        uploadStepsTip: 'Upload solution steps (optional)',
        uploadedImages: 'Uploaded images:',
        previewImageAlt: 'Uploaded image ',
        preview: 'Preview',
        delete: 'Delete',
        inputAnswer: 'Enter your answer',
        answerTip: '(Enter an answer, e.g., A–E or a value)',
        correctAnswer: 'Correct answer:',
        analysis: 'Analysis:',
        prev: 'Previous',
        next: 'Next',
        submitCurrent: 'Submit current',
        submitAll: 'Submit answered',
        reset: 'Reset',
        emptyKnowledge: 'No questions for this topic',
        backKnowledge: 'Back to topics',
        imagePreview: 'Image Preview',
        resultTitle: 'Results',
        close: 'Close',
        retry: 'Retry',
        totalLabel: 'Total:',
        correctLabel: 'Correct:',
        wrongLabel: 'Wrong:',
        accuracyLabel: 'Accuracy:',
        done: 'Done',
        correctShort: 'Correct',
        wrongShort: 'Wrong'
      }
      const dict = this.langMode === 'en' ? en : zh
      return dict[key] || key
    },

    // 题号文本
    formatQuestionNumber(index) {
      const n = Number(index) + 1
      if (this.langMode === 'en') return `Question ${n}`
      if (this.langMode === 'bi') return `第 ${n} 题 / Question ${n}`
      return `第 ${n} 题`
    },

    getDifficultyClass(difficulty) {
      const d = Number(difficulty)
      if (!isFinite(d)) return 'difficulty-medium'
      if (d <= 0.25) return 'difficulty-beginner'
      if (d <= 0.5) return 'difficulty-easy'
      if (d <= 0.75) return 'difficulty-medium'
      return 'difficulty-hard'
    },

    getDifficultyText(difficulty) {
      const d = Number(difficulty)
      if (!isFinite(d)) return this.langMode === 'en' ? 'Medium' : '中等'
      if (d <= 0.25) return this.langMode === 'en' ? 'Beginner' : '入门'
      if (d <= 0.5) return this.langMode === 'en' ? 'Easy' : '简单'
      if (d <= 0.75) return this.langMode === 'en' ? 'Medium' : '中等'
      return this.langMode === 'en' ? 'Hard' : '困难'
    },

    getAnswerStatusClass(index) {
      if (!this.questionList[index]) return ''
      if (!this.userAnswers[index] || String(this.userAnswers[index]).trim() === '') return ''
      return this.isAnswerCorrect(index) ? 'status-correct' : 'status-wrong'
    },

    getAnswerStatusText(index) {
      if (!this.questionList[index]) return ''
      if (!this.userAnswers[index] || String(this.userAnswers[index]).trim() === '') return ''
      return this.isAnswerCorrect(index) ? this.tt('correctShort') : this.tt('wrongShort')
    },

    // 答案变化处理
    onAnswerChange(index) {
      const val = this.userAnswers[index]
      const q = this.questionList[index]
      if (!q) return
      if (val && String(val).trim() !== '') {
        this.$set(this.doneMap, q.id, true)
      } else {
        if (this.doneMap[q.id]) this.$delete(this.doneMap, q.id)
      }
      try { this.saveProgress() } catch (_) {}
      console.log(`第${index + 1}题答案:`, this.userAnswers[index])
    },

    startQuestionTimer() {
      try {
        if (this.timerInterval) clearInterval(this.timerInterval)
      } catch (_) {}
      this.currentStartTime = Date.now()
      this.currentQuestionTime = '00:00'
      this.timerColor = '#409EFF'
      this.timerInterval = setInterval(() => {
        try {
          const elapsed = Math.max(0, Math.floor((Date.now() - this.currentStartTime) / 1000))
          const m = String(Math.floor(elapsed / 60)).padStart(2, '0')
          const s = String(elapsed % 60).padStart(2, '0')
          this.currentQuestionTime = `${m}:${s}`
          if (elapsed >= 300) this.timerColor = '#F56C6C'
          else if (elapsed >= 180) this.timerColor = '#E6A23C'
          else this.timerColor = '#409EFF'
        } catch (_) {}
      }, 1000)
    },

    stopQuestionTimer() {
      try {
        if (this.timerInterval) clearInterval(this.timerInterval)
      } catch (_) {}
      this.timerInterval = null
    },

    recordQuestionTime() {
      try {
        if (this.currentStartTime && Array.isArray(this.questionDurations)) {
          const elapsed = Math.max(0, Math.floor((Date.now() - this.currentStartTime) / 1000))
          this.$set(this.questionDurations, this.currentIndex, elapsed)
        }
      } catch (_) {}
    },

    async persistSingleAnswerRecord(r) {
      const userId = this.currentUserId
      if (!userId || !r) return
      const repoId = Number(this.repoId) || this.repoId || (this.$route.query && this.$route.query.repoId) || null

      const q = this.questionList.find(x => x && x.id === r.questionId)
      const cn = q ? (q.contentCn || q.content || '') : (r.questionContent || '')
      const en = q ? (q.contentEn || q.content || '') : (r.questionContent || '')

      const idx = this.questionList.findIndex(x => x && x.id === r.questionId)
      const elapsedMs = this.currentStartTime ? Math.max(0, Date.now() - this.currentStartTime) : 0
      const durationMs = idx >= 0 && Array.isArray(this.questionDurations)
        ? Math.max(0, Number(this.questionDurations[idx] || 0) * 1000)
        : elapsedMs

      let imagesJson = ''
      try {
        const imgs = idx >= 0 ? (this.questionImages && this.questionImages[idx]) : null
        if (Array.isArray(imgs) && imgs.length > 0) {
          imagesJson = JSON.stringify(imgs.map(x => x && x.url ? x.url : x).filter(Boolean))
        }
      } catch (_) {}

      const payload = {
        userId,
        quesId: r.questionId,
        quescontentCn: cn,
        quescontentEn: en,
        userAnswer: r.userAnswer,
        answer: r.correctAnswer,
        judgeCn: r.analysis || '',
        judgeEn: '',
        mynote: '',
        isReview: 0,
        knowledge: this.getKnowledgeDisplayWithParent(r.knowledge) || '',
        isRight: r.isCorrect ? 1 : 0,
        repoId,
        time: new Date().toISOString(),
        duration: String(durationMs || 0),
        images: imagesJson
      }

      await addUserQuestionRecord(payload)
    },

    prevQuestion() {
      if (this.currentIndex <= 0) return
      this.stopQuestionTimer()
      this.currentIndex -= 1
      this.startQuestionTimer()
    },

    nextQuestion() {
      if (!Array.isArray(this.questionList) || this.currentIndex >= this.questionList.length - 1) return
      this.stopQuestionTimer()
      this.currentIndex += 1
      this.startQuestionTimer()
    },

    async persistStatusSingle(r) {
      const userId = this.currentUserId
      if (!userId || !r) return
      const repoId = Number(this.repoId) || this.repoId || (this.$route.query && this.$route.query.repoId) || null
      const payload = {
        userId,
        quesList: String(r.questionId || ''),
        count: 1,
        rightCount: r.isCorrect ? 1 : 0,
        source: '知识点练习',
        repoId
      }
      try { await userStatusAdd(payload) } catch (_) {}
    },

    getTodayDate() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    async updateSchemeFinishedIds(userId, repoId, ids) {
      if (!userId || !repoId || !ids || !ids.length) return
      const resp = await getSchemeByUserIdAndRepoId(userId, repoId)
      const scheme = resp?.data?.data ?? resp?.data ?? resp
      const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
      if (!schemeId) return
      const existing = scheme?.finishedId || scheme?.finishedIds || scheme?.doneIds || ''
      const existSet = new Set(String(existing || '').split(/[，,\s]+/).filter(x => x && !isNaN(Number(x))).map(x => Number(x)))
      ids.forEach(id => existSet.add(Number(id)))
      const merged = Array.from(existSet).filter(n => !isNaN(n) && n > 0).join(',')
      await updateSchemeFinished(schemeId, merged)
    },

    async reportDailyProgress() {
      try {
        const userId = this.currentUserId
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal) return
        let progressId = this.statProgressId
        if (!progressId) {
          try {
            const idRes = await getProgressByDate(userId, repoIdVal, this.getTodayDate())
            const idData = idRes?.data?.data ?? idRes?.data ?? idRes
            progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
            if (!progressId) {
              const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: this.getTodayDate() })
              const planData = planRes?.data?.data ?? planRes?.data ?? planRes
              progressId = planData && (planData.id || planData.ID)
            }
            this.statProgressId = progressId || null
          } catch (_) {}
        }
        if (!progressId) return
        const finishedIdsStr = Array.isArray(this.statFinishedIds) && this.statFinishedIds.length
          ? this.statFinishedIds.join(',')
          : ''
        await updateProgressFinished(
          progressId,
          finishedIdsStr,
          Number(this.statCorrect) || 0,
          Number(this.statWrong) || 0
        )
      } catch (_) {}
    },

    async syncStudyPlanAfterSubmit(results) {
      try {
        const userId = this.currentUserId
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal || !Array.isArray(results) || !results.length) return

        const ids = results
          .map(r => Number(r && r.questionId))
          .filter(id => !isNaN(id) && id > 0)

        if (!ids.length) return

        await this.updateSchemeFinishedIds(userId, repoIdVal, ids)

        const todayStr = this.getTodayDate()
        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        let progressId = first ? (first.id ?? first.progressId) : null

        if (!progressId) {
          const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: todayStr })
          const planData = planRes?.data?.data ?? planRes?.data ?? planRes
          progressId = planData && (planData.id || planData.ID)
        }

        this.statProgressId = progressId || this.statProgressId

        const currentFinished = String(first?.finished_id ?? first?.finishedId ?? '')
        const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
        ;(Array.isArray(this.statFinishedIds) ? this.statFinishedIds : []).forEach(id => finishedSet.add(Number(id)))
        ids.forEach(id => finishedSet.add(id))
        this.statFinishedIds = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0)

        const correctDelta = results.filter(r => r && r.isCorrect).length
        const wrongDelta = results.filter(r => r && !r.isCorrect).length
        const baseCorrect = Number(first?.correct_count ?? first?.correctCount ?? 0)
        const baseWrong = Number(first?.wrong_count ?? first?.wrongCount ?? 0)
        this.statCorrect = baseCorrect + correctDelta
        this.statWrong = baseWrong + wrongDelta

        await this.reportDailyProgress()
        try { await this.loadKnowledgeProgress() } catch (_) {}
      } catch (_) {}
    },

    // 检查答案是否正确
    isAnswerCorrect(index) {
      const userAnswer = this.userAnswers[index]
      const correctAnswer = this.questionList[index].answer
      return userAnswer === correctAnswer
    },

    // 逐题提交当前题
    async submitCurrentAnswer() {
      const idx = this.currentIndex
      const ans = this.userAnswers[idx]
      if (!ans || String(ans).trim() === '') {
        this.$message.warning('请先作答本题')
        return
      }
      // 记录当前题目用时
      this.recordQuestionTime()
      this.stopQuestionTimer()
      const q = this.questionList[idx]
      const result = {
        questionId: q.id,
        questionContent: this.getQuestionContent(q),
        userAnswer: ans,
        correctAnswer: q.answer,
        isCorrect: ans === q.answer,
        analysis: this.getQuestionAnalysis(q),
        knowledge: q.knowledge
      }
      const pos = this.questionResults.findIndex(r => r.questionId === q.id)
      if (pos >= 0) {
        this.$set(this.questionResults, pos, result)
      } else {
        this.questionResults.push(result)
      }
      this.$set(this.revealedQuestions, idx, true)
      // 标记完成并保存进度
      this.$set(this.doneMap, q.id, true)
      this.saveProgress()
      try { await this.persistSingleAnswerRecord(result) } catch (_) {}
      try { await this.persistStatusSingle(result) } catch (_) {}
      try { await this.syncStudyPlanAfterSubmit([result]) } catch (_) {}
      this.$message.success(result.isCorrect ? '答对了！' : '已提交，答案已显示')
    },

    async submitAnswers() {
      if (this.submitting) return
      this.submitting = true
      try {
        this.stopQuestionTimer()
        const results = []
        this.questionList.forEach((q, idx) => {
          const ans = this.userAnswers[idx]
          if (!q || !ans || String(ans).trim() === '') return
          const r = {
            questionId: q.id,
            questionContent: this.getQuestionContent(q),
            userAnswer: ans,
            correctAnswer: q.answer,
            isCorrect: ans === q.answer,
            analysis: this.getQuestionAnalysis(q),
            knowledge: q.knowledge
          }
          results.push(r)
          this.$set(this.revealedQuestions, idx, true)
          this.$set(this.doneMap, q.id, true)
        })
        this.questionResults = results
        this.showAnswers = true
        this.saveProgress()

        await this.persistBatchAnswerRecords()
        await this.persistBatchStatus()
        await this.syncStudyPlanAfterSubmit(this.questionResults)

        this.wrongQuestions = this.questionResults
          .filter(r => !r.isCorrect)
          .map(r => ({
            questionId: r.questionId,
            knowledge: r.knowledge,
            questionContent: this.renderMathFormula(r.questionContent),
            userAnswer: r.userAnswer,
            correctAnswer: r.correctAnswer,
            analysis: r.analysis
          }))
        this.showResultDialog = true
      } catch (e) {
        this.$message.error('提交失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },

    // 批量写入用户题目记录（逐题）
    async persistBatchAnswerRecords() {
      const userId = this.currentUserId
      const repoId = Number(this.repoId) || this.repoId
      if (!userId || !Array.isArray(this.questionResults)) return
      const tasks = this.questionResults.map(r => {
        const knowledgeStr = this.getKnowledgeDisplayWithParent(r.knowledge)
        const payload = {
          userId,
          quesId: r.questionId,
          quesContent: r.questionContent,
          userAnswer: r.userAnswer,
          answer: r.correctAnswer,
          judge: r.analysis || '',
          mynote: '',
          isReview: 0,
          knowledge: knowledgeStr || '',
          isRight: r.isCorrect ? 1 : 0,
          repoId,
          time: new Date().toISOString()
        }
        return addUserQuestionRecord(payload).catch(() => null)
      })
      await Promise.allSettled(tasks)
    },

    // 写入用户状态（一次汇总）
    async persistBatchStatus() {
      const userId = this.currentUserId
      if (!userId || !Array.isArray(this.questionResults)) return
      const ids = this.questionResults.map(r => r.questionId).filter(Boolean)
      const rightCount = this.questionResults.filter(r => r.isCorrect).length
      const repoId = Number(this.repoId) || this.repoId || (this.$route.query && this.$route.query.repoId) || null
      const payload = {
        userId,
        quesList: ids.join(',') || '',
        count: this.questionResults.length,
        rightCount,
        source: '知识点练习',
        repoId
      }
      await userStatusAdd(payload)
    },

    // 关闭结果弹窗
    handleCloseResultDialog() {
      this.showResultDialog = false
      this.wrongQuestions = []
    },

    resetAnswers() {
      try {
        this.userAnswers = new Array(this.questionList.length).fill('')
        this.questionResults = []
        this.revealedQuestions = new Array(this.questionList.length).fill(false)
        this.doneMap = {}
        this.saveProgress()
        this.currentIndex = 0
        this.startQuestionTimer()
      } catch (_) {}
    },

    // 重新练习
    retryPractice() {
      this.showResultDialog = false
      this.resetAnswers()
    },

    // 获取错题在原题目列表中的索引
    getOriginalQuestionIndex(questionId) {
      if (!this.questionList || !questionId) return 0
      const index = this.questionList.findIndex(q => q.id === questionId)
      return index >= 0 ? index : 0
    },

    // 刷新题目
    refreshQuestions() {
      this.loadQuestions()
    },

    // 进度存取（本地）
    calcProgressKey() {
      const userId = this.currentUserId || 'anon'
      const repoId = this.repoId || (this.$route.query && this.$route.query.repoId) || 'repo'
      const knowledgeId = (this.$route.query && (this.$route.query.knowledgeId || this.$route.query.knowledge)) || this.currentKnowledge || 'knowledge'
      return `practice_progress_${userId}_${repoId}_${knowledgeId}`
    },
    saveProgress() {
      try {
        const key = this.calcProgressKey()
        const data = { doneMap: this.doneMap }
        window.localStorage.setItem(key, JSON.stringify(data))
      } catch (_) {}
    },
    restoreProgress() {
      try {
        const key = this.calcProgressKey()
        const str = window.localStorage.getItem(key)
        if (!str) { this.doneMap = {}; return }
        const obj = JSON.parse(str)
        this.doneMap = obj && obj.doneMap && typeof obj.doneMap === 'object' ? obj.doneMap : {}
      } catch (_) { this.doneMap = {} }
      // 将 currentIndex 定位到第一道未完成的题
      const idx = this.questionList.findIndex(q => !this.doneMap[q.id])
      this.currentIndex = idx >= 0 ? idx : 0
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 渲染数学公式
    renderMathFormula(text) {
      if (!text) return ''

      try {
        let html = String(text)
        // 1) 行内公式: \(...\)
        // 修复：使用 [\s\S]+? 而不是 [^)]+ 以正确匹配包含括号的公式
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: false })
          } catch (e) {
            return match
          }
        })
        // 2) 块级公式: \[...\]
        // 修复：使用 [\s\S]+? 而不是 [^\]]+ 以正确匹配复杂公式
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: true })
          } catch (e) {
            return match
          }
        })
        // 3) 块级公式: $$...$$
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: true })
          } catch (e) {
            return match
          }
        })
        // 4) 行内公式: $...$ （避免与 $$...$$ 冲突）
        html = html.replace(/\$(?!\$)([^$]+?)\$(?!\$)/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: false })
          } catch (e) {
            return match
          }
        })
        return html
      } catch (e) {
        return String(text)
      }
    },

    // 图片上传前验证
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB!')
        return false
      }
      return true
    },

    // 图片上传成功
    handleImageUploadSuccess(response, file, questionIndex) {
      console.log('图片上传成功:', response)
      if (response && response.data && response.data.url) {
        if (!this.questionImages[questionIndex]) {
          this.$set(this.questionImages, questionIndex, [])
        }
        this.questionImages[questionIndex].push({
          url: response.data.url,
          name: file.name,
          size: file.size
        })
        this.$message.success('图片上传成功!')
      } else {
        this.$message.error('图片上传失败，请重试')
      }
    },

    // 图片上传失败
    handleImageUploadError(error) {
      console.error('图片上传失败:', error)
      this.$message.error('图片上传失败，请重试')
    },

    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url
      this.showImagePreview = true
    },

    // 删除图片
    removeImage(questionIndex, imageIndex) {
      this.$confirm('确定要删除这张图片吗？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.questionImages[questionIndex].splice(imageIndex, 1)
        this.$message.success('图片已删除')
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    }
  }
}
</script>

<style scoped>
.knowledge-question-practice {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 0;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 顶部导航栏 */
.practice-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-right {
  display: flex;
  align-items: center;
}

.back-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #f5f5f5;
}

.practice-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.refresh-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 加载状态 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* 错误状态 */
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-content {
  text-align: center;
  padding: 40px;
}

.error-icon {
  font-size: 48px;
  color: #f56c6c;
  margin-bottom: 20px;
}

.error-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

/* 空状态 */
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.empty-content {
  text-align: center;
  padding: 40px;
}

.empty-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

/* 统计信息 */
.stats-section {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stat-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
}

/* 题目练习区域 */
.question-practice-content {
  padding: 20px;
}

.questions-container {
  margin-bottom: 30px;
}

.question-item {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.question-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.question-item.answered {
  border-color: #409eff;
  background: #f8f9ff;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.question-number {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.question-knowledge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
  background-color: #409eff;
}

.question-source {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  background-color: #f4f4f5;
  border: 1px solid #e4e7ed;
}

.question-source i {
  margin-right: 4px;
  color: #909399;
}

.question-difficulty {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
}

.difficulty-beginner {
  background-color: #95d475;
}

.difficulty-easy {
  background-color: #67c23a;
}

.difficulty-medium {
  background-color: #e6a23c;
}

.difficulty-hard {
  background-color: #f56c6c;
}

.answer-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
  font-weight: bold;
}

.status-correct {
  background-color: #67c23a;
}

.status-incorrect {
  background-color: #f56c6c;
}

.question-content {
  margin-bottom: 20px;
  line-height: 1.6;
  color: #333;
  font-size: 15px;
}

/* 图片上传区域 */
.image-upload-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
}

.upload-button-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.image-uploader {
  display: inline-block;
}

.upload-tip {
  font-size: 12px;
  color: #999;
}

.uploaded-images {
  margin-top: 15px;
}

.image-preview-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 10px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fff;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.image-actions .el-button {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.image-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 图片预览弹窗 */
.image-preview-dialog {
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.question-options {
  margin-bottom: 15px;
}

.option-item {
  display: block;
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.option-image-wrap {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.option-key-label {
  min-width: 20px;
  font-weight: 600;
  color: #303133;
  line-height: 24px;
}

.option-image-preview {
  width: 100%;
  max-width: 240px;
  max-height: 220px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.option-item:hover {
  background-color: #f5f7fa;
}

.question-answer-input {
  margin-bottom: 15px;
}

.answer-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.correct-answer {
  margin-top: 15px;
  padding: 10px;
  background: #f0f9ff;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.answer-label {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 5px;
}

.answer-value {
  color: #67c23a;
  font-weight: bold;
}

.question-analysis {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.analysis-title {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 10px;
  font-size: 14px;
}

.analysis-content {
  line-height: 1.6;
  color: #606266;
  font-size: 14px;
}

/* 操作按钮区域 */
.action-section {
  text-align: center;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-section .el-button {
  margin: 0 10px;
}

/* 底部逐题控件美化 */
.step-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}
.step-progress {
  font-weight: 600;
  color: #606266;
}

/* 结果弹窗 */
.result-dialog-content .result-summary {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 12px;
  color: white;
}

.result-dialog-content .score-display {
  display: flex;
  align-items: baseline;
  margin-right: 40px;
}

.result-dialog-content .score-number {
  font-size: 48px;
  font-weight: bold;
}

.result-dialog-content .score-label {
  font-size: 24px;
  margin-left: 5px;
}

.result-dialog-content .score-details {
  flex: 1;
}

.result-dialog-content .detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.result-dialog-content .detail-label {
  font-size: 14px;
  opacity: 0.9;
}

.result-dialog-content .detail-value {
  font-weight: bold;
}

.result-dialog-content .detail-value.correct {
  color: #67c23a;
}

.result-dialog-content .detail-value.incorrect {
  color: #f56c6c;
}

.wrong-questions-section {
  margin-top: 30px;
}

.wrong-questions-section h4 {
  color: #f56c6c;
  margin-bottom: 20px;
  font-size: 18px;
}

.wrong-question-item {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background-color: #fafafa;
}

.wrong-question-item .question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.wrong-question-item .question-number {
  font-weight: bold;
  color: #303133;
  font-size: 16px;
}

.wrong-question-item .question-knowledge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
  background-color: #f56c6c;
}

.wrong-question-item .question-content {
  margin-bottom: 20px;
  line-height: 1.6;
  color: #303133;
}

.wrong-question-item .answer-details {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #f56c6c;
}

.wrong-question-item .answer-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.wrong-question-item .answer-label {
  width: 80px;
  color: #606266;
  font-size: 14px;
}

.wrong-question-item .answer-value {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 14px;
}

.wrong-question-item .answer-value.correct {
  background-color: #f0f9ff;
  color: #67c23a;
  border: 1px solid #b3d8ff;
}

.wrong-question-item .answer-value.incorrect {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}

.wrong-question-item .analysis-section {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.wrong-question-item .analysis-title {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 10px;
  font-size: 14px;
}

.wrong-question-item .analysis-content {
  line-height: 1.6;
  color: #606266;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .practice-header {
    padding: 10px 15px;
    flex-direction: column;
    gap: 10px;
  }

  .header-left {
    width: 100%;
    justify-content: space-between;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .stats-section {
    flex-direction: column;
    gap: 15px;
    padding: 15px;
  }

  .question-practice-content {
    padding: 15px;
  }

  .question-item {
    padding: 15px;
  }

  .action-section .el-button {
    margin: 5px;
    width: 100%;
  }

  .result-dialog-content .result-summary {
    flex-direction: column;
    text-align: center;
  }

  .score-display {
    margin-right: 0;
    margin-bottom: 20px;
  }

  .image-upload-section {
    padding: 10px;
  }

  .upload-button-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .image-list {
    gap: 10px;
  }

  .image-item {
    width: 80px;
    height: 80px;
  }
}
</style>
